import { AppBar, Toolbar, IconButton, Box, Button, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate } from "react-router-dom";
import { KeyboardArrowLeft } from "@mui/icons-material";
import SettingsBrightnessIcon from "@mui/icons-material/SettingsBrightness";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import { useSelector, useDispatch } from "react-redux";
import { selectCurrentUser, logout } from "../pages/auth/authSlice";
import { useGetTeacherSubjectsQuery } from "../../app/questionApiSlice";
import { useGetClassesQuery } from "../pages/classroom/classApiSlice";
import { apiSlice } from "../../app/apiSlice";
import { attemptOfflineAwareLogout } from "../../app/syncService";
import TeacherAiWalletChip from "../customComponents/TeacherAiWalletChip";
import { setColorMode, selectColorMode } from "../../theme/themeSlice";

function LmsHeader({ drawerWidth, handleDrawerToggle }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const colorMode = useSelector(selectColorMode);

  const modeSequence = ["system", "light", "dark"];
  const handleModeToggle = () => {
    const next = modeSequence[(modeSequence.indexOf(colorMode) + 1) % modeSequence.length];
    dispatch(setColorMode(next));
  };

  const modeIcon = colorMode === "light"
    ? <WbSunnyOutlinedIcon fontSize="small" />
    : colorMode === "dark"
    ? <NightsStayOutlinedIcon fontSize="small" />
    : <SettingsBrightnessIcon fontSize="small" />;

  const modeLabel = colorMode === "light"
    ? "Light mode"
    : colorMode === "dark"
    ? "Dark mode"
    : "System default";

  const userRoles = user?.roles?.map((item) => item?.role?.name).filter(Boolean) || [];
  const isTeacher = userRoles.includes("teacher");
  const isReviewer = userRoles.includes("reviewer");
  const STAFF_ROLES = ["admin", "examiner", "accountant", "webmaster", "agent"];
  const isAdminLike = userRoles.some((r) => STAFF_ROLES.includes(r));
  const isReviewerOnly = isReviewer && !isAdminLike && !userRoles.includes("teacher");
  const isTeacherReviewerOnly = isTeacher && isReviewer && !isAdminLike &&
    userRoles.every((r) => r === "teacher" || r === "reviewer");

  // Only fetch if needed — results will be served from cache if LmsSideNav already loaded them
  const { data: teacherSubjects } = useGetTeacherSubjectsQuery(
    undefined,
    { skip: !isTeacherReviewerOnly }
  );
  const { data: myClasses } = useGetClassesQuery(
    "all/mine",
    { skip: !isTeacherReviewerOnly }
  );

  const handleExitLms = async () => {
    const isStudent = userRoles.includes("student");
    const hasSubjectsOrClasses = teacherSubjects?.length > 0 || myClasses?.length > 0;

    if (isStudent) {
      dispatch(logout());
      dispatch(apiSlice.util.resetApiState());
      navigate("/login");
    } else if (isReviewerOnly || (isTeacherReviewerOnly && !hasSubjectsOrClasses)) {
      const result = await attemptOfflineAwareLogout();
      if (!result.allowed) {
        const discard = window.confirm(
          "You have unsynced offline report changes on this device. Stay signed in to sync them later, or press OK to discard them and continue logging out."
        );
        if (!discard) return;
        await attemptOfflineAwareLogout({ forceDiscard: true });
      }
      dispatch(logout());
      dispatch(apiSlice.util.resetApiState());
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={(theme) => ({
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        boxShadow: "none",
        backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#e7e7e7"}`,
      })}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { sm: "none" } }}
        >
          <MenuIcon sx={{ color: "primary.safeOnLight" }} />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />
        {isTeacher && <TeacherAiWalletChip />}
        <Tooltip title={modeLabel}>
          <IconButton onClick={handleModeToggle} sx={{ color: "primary.safeOnLight", ml: 1 }}>
            {modeIcon}
          </IconButton>
        </Tooltip>
        <Button
          endIcon={<KeyboardArrowLeft />}
          onClick={handleExitLms}
          sx={{
            textTransform: "capitalize",
            color: "primary.safeOnLight",
            fontSize: "0.9rem",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.05)",
            },
          }}
        >
          Exit LMS
        </Button>
      </Toolbar>
    </AppBar>
  );
}

export default LmsHeader;
